import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import Moveable from 'react-moveable'
import { cn } from '@/lib/utils'

export interface SpatialItem {
  id: string
  type: 'clip' | 'text'
  label: string
  x: number      // percentage 0-100
  y: number      // percentage 0-100
  width: number   // percentage 0-100
  height: number  // percentage 0-100
  rotation: number
  zIndex: number
  opacity: number
  locked?: boolean
  thumbnailUrl?: string
}

interface SpatialCanvasProps {
  items: SpatialItem[]
  selectedId: string | null
  onSelect: (id: string | null) => void
  onUpdate: (id: string, changes: Partial<SpatialItem>) => void
  onLiveUpdate?: (id: string, changes: Partial<SpatialItem>) => void
  containerWidth: number
  containerHeight: number
  compositionWidth: number
  compositionHeight: number
}

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val))
}

export function SpatialCanvas({
  items,
  selectedId,
  onSelect,
  onUpdate,
  onLiveUpdate,
  containerWidth,
  containerHeight,
  compositionWidth,
  compositionHeight,
}: SpatialCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null)
  const targetRef = useRef<HTMLDivElement>(null)
  const moveableRef = useRef<Moveable>(null)
  const [measuredSize, setMeasuredSize] = useState({ w: containerWidth, h: containerHeight })

  useEffect(() => {
    if (!canvasRef.current) return
    const obs = new ResizeObserver(([entry]) => {
      setMeasuredSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    obs.observe(canvasRef.current)
    return () => obs.disconnect()
  }, [])

  const effectiveWidth = measuredSize.w > 0 ? measuredSize.w : containerWidth
  const effectiveHeight = measuredSize.h > 0 ? measuredSize.h : containerHeight

  const compAspect = compositionWidth / compositionHeight

  const cw = effectiveWidth
  const ch = effectiveHeight
  const containerAspectActual = cw / (ch || 1)

  let renderWidth: number, renderHeight: number, offsetX: number, offsetY: number
  if (cw <= 0 || ch <= 0) {
    renderWidth = 640
    renderHeight = 360
    offsetX = 0
    offsetY = 0
  } else if (containerAspectActual > compAspect) {
    renderHeight = ch
    renderWidth = ch * compAspect
    offsetX = (cw - renderWidth) / 2
    offsetY = 0
  } else {
    renderWidth = cw
    renderHeight = cw / compAspect
    offsetX = 0
    offsetY = (ch - renderHeight) / 2
  }

  const selectedItem = useMemo(() => items.find(i => i.id === selectedId), [items, selectedId])
  const [, forceRender] = useState(0)
  useEffect(() => {
    if (selectedId) {
      requestAnimationFrame(() => {
        forceRender(n => n + 1)
        moveableRef.current?.updateTarget()
      })
    }
  }, [selectedId])

  const pxToPercent = useCallback((px: number, dimension: 'x' | 'y') => {
    return dimension === 'x'
      ? (px / renderWidth) * 100
      : (px / renderHeight) * 100
  }, [renderWidth, renderHeight])

  const percentToPx = useCallback((pct: number, dimension: 'x' | 'y') => {
    return dimension === 'x'
      ? (pct / 100) * renderWidth
      : (pct / 100) * renderHeight
  }, [renderWidth, renderHeight])

  const handleItemClick = useCallback((e: React.MouseEvent, id: string) => {
    e.stopPropagation()
    onSelect(id)
  }, [onSelect])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
      if (!selectedItem) return

      if (e.key === 'Escape') {
        e.preventDefault()
        onSelect(null)
        return
      }

      const nudge = e.shiftKey ? 5 : 1

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          onUpdate(selectedItem.id, { x: clamp(selectedItem.x - nudge, -50, 150) })
          break
        case 'ArrowRight':
          e.preventDefault()
          onUpdate(selectedItem.id, { x: clamp(selectedItem.x + nudge, -50, 150) })
          break
        case 'ArrowUp':
          e.preventDefault()
          onUpdate(selectedItem.id, { y: clamp(selectedItem.y - nudge, -50, 150) })
          break
        case 'ArrowDown':
          e.preventDefault()
          onUpdate(selectedItem.id, { y: clamp(selectedItem.y + nudge, -50, 150) })
          break
        case '[':
          e.preventDefault()
          onUpdate(selectedItem.id, { zIndex: selectedItem.zIndex - 1 })
          break
        case ']':
          e.preventDefault()
          onUpdate(selectedItem.id, { zIndex: selectedItem.zIndex + 1 })
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedId, selectedItem, onSelect, onUpdate])

  return (
    <>
      <style>{`
        .moveable-control-box,
        .moveable-control-box * {
          pointer-events: auto !important;
        }
      `}</style>
      <div
        ref={canvasRef}
        className="absolute inset-0"
        style={{ pointerEvents: 'none', zIndex: 40 }}
      >
        {/* Composition area */}
        <div
          className="absolute"
          style={{
            left: offsetX,
            top: offsetY,
            width: renderWidth,
            height: renderHeight,
          }}
        >
          {/* Spatial items */}
          {items.map((item) => {
            const isSelected = item.id === selectedId
            const left = percentToPx(item.x, 'x')
            const top = percentToPx(item.y, 'y')
            const w = percentToPx(item.width, 'x')
            const h = percentToPx(item.height, 'y')

            return (
              <div
                key={item.id}
                ref={isSelected ? targetRef : undefined}
                className={cn(
                  'absolute group/item',
                  isSelected ? 'cursor-move' : 'cursor-pointer',
                  item.locked && 'cursor-not-allowed',
                )}
                style={{
                  left,
                  top,
                  width: w,
                  height: h,
                  transform: `rotate(${item.rotation}deg)`,
                  zIndex: item.zIndex + 100,
                  opacity: item.opacity,
                  pointerEvents: 'auto',
                }}
                onClick={(e) => handleItemClick(e, item.id)}
              >
                {/* Hover/select border */}
                <div className={cn(
                  'absolute inset-0 rounded-sm border-2 transition-colors',
                  isSelected
                    ? 'border-blue-500'
                    : 'border-transparent group-hover/item:border-white/40',
                )} />

                {/* Hover label */}
                {!isSelected && (
                  <div className="absolute -top-5 left-0 text-[9px] font-medium bg-black/70 px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover/item:opacity-100 transition-opacity pointer-events-none text-white/80">
                    {item.label}
                  </div>
                )}
              </div>
            )
          })}

          {/* Moveable for selected item */}
          {selectedItem && targetRef.current && !selectedItem.locked && (
            <Moveable
              ref={moveableRef}
              key={selectedItem.id}
              target={targetRef.current}
              draggable
              resizable
              rotatable
              throttleDrag={0}
              throttleResize={0}
              throttleRotate={1}
              edge={false}
              keepRatio={false}
              renderDirections={['nw', 'n', 'ne', 'w', 'e', 'sw', 's', 'se']}
              onDrag={({ target, left, top }) => {
                target.style.left = `${left}px`
                target.style.top = `${top}px`
                onLiveUpdate?.(selectedItem.id, {
                  x: clamp(pxToPercent(left, 'x'), -50, 150),
                  y: clamp(pxToPercent(top, 'y'), -50, 150),
                })
              }}
              onDragEnd={({ lastEvent }) => {
                if (!lastEvent) return
                const x = clamp(pxToPercent(lastEvent.left, 'x'), -50, 150)
                const y = clamp(pxToPercent(lastEvent.top, 'y'), -50, 150)
                onUpdate(selectedItem.id, { x, y })
              }}
              onResize={({ target, width, height, drag }) => {
                target.style.width = `${width}px`
                target.style.height = `${height}px`
                target.style.left = `${drag.left}px`
                target.style.top = `${drag.top}px`
                onLiveUpdate?.(selectedItem.id, {
                  width: clamp(pxToPercent(width, 'x'), 5, 200),
                  height: clamp(pxToPercent(height, 'y'), 5, 200),
                  x: clamp(pxToPercent(drag.left, 'x'), -50, 150),
                  y: clamp(pxToPercent(drag.top, 'y'), -50, 150),
                })
              }}
              onResizeEnd={({ lastEvent }) => {
                if (!lastEvent) return
                const w = clamp(pxToPercent(lastEvent.width, 'x'), 5, 200)
                const h = clamp(pxToPercent(lastEvent.height, 'y'), 5, 200)
                const x = clamp(pxToPercent(lastEvent.drag.left, 'x'), -50, 150)
                const y = clamp(pxToPercent(lastEvent.drag.top, 'y'), -50, 150)
                onUpdate(selectedItem.id, { width: w, height: h, x, y })
              }}
              onRotate={({ target, transform, rotation }) => {
                target.style.transform = transform
                onLiveUpdate?.(selectedItem.id, { rotation: Math.round(rotation * 10) / 10 })
              }}
              onRotateEnd={({ lastEvent }) => {
                if (!lastEvent) return
                onUpdate(selectedItem.id, { rotation: Math.round(lastEvent.rotation * 10) / 10 })
              }}
            />
          )}

        </div>
      </div>
    </>
  )
}
